import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import PhotoCropModal from '../../components/ui/PhotoCropModal';
import { IconShieldCheck, IconCheck, IconCamera } from '@tabler/icons-react';

const STEPS = [
  'Your photo',
  'Organisation details',
  'Your first site',
  'Ready',
];

export default function OnboardingAdmin() {
  const navigate = useNavigate();
  const { user, userProfile, org, orgId, refreshProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Step 0 — photo
  const [showCropModal, setShowCropModal] = useState(false);
  const [photoURL, setPhotoURL] = useState(null);

  // Step 1 — org details
  const [orgName, setOrgName] = useState(org?.name || '');
  const [tradingName, setTradingName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [town, setTown] = useState('');
  const [county, setCounty] = useState('');
  const [postcode, setPostcode] = useState('');
  const [industryType, setIndustryType] = useState('farm');

  // Step 2 — first site
  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');

  const resolvedOrgId = orgId || userProfile?.orgId;

  const handlePhotoSave = async (blob) => {
    try {
      const storageRef = ref(storage, `organisations/${resolvedOrgId}/avatars/${user.uid}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      await updateDoc(
        doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
        { photoURL: url, updatedAt: serverTimestamp() }
      );
      setPhotoURL(url);
      await refreshProfile();
    } catch (err) {
      console.error(err);
    }
  };

  const handleNext = async () => {
    setError('');

    if (step === 1 && !orgName.trim()) {
      setError('Organisation name is required');
      return;
    }

    if (step === 2 && !siteName.trim()) {
      setError('Site name is required');
      return;
    }

    if (step === 2) {
      setSaving(true);
      try {
        // Save org details
        await updateDoc(doc(db, 'organisations', resolvedOrgId), {
          name: orgName.trim(),
          tradingName: tradingName.trim() || null,
          companyNumber: companyNumber.trim() || null,
          vatNumber: vatNumber.trim() || null,
          phone: phone.trim() || null,
          website: website.trim() || null,
          address: {
            line1: address1.trim(),
            line2: address2.trim() || null,
            town: town.trim(),
            county: county.trim() || null,
            postcode: postcode.trim(),
            country: 'United Kingdom',
          },
          industryType,
          updatedAt: serverTimestamp(),
        });

        // Create first site
        const { addDoc, collection } = await import('firebase/firestore');
        const { db: database } = await import('../../firebase');
        await addDoc(
          collection(database, 'organisations', resolvedOrgId, 'sites'),
          {
            name: siteName.trim(),
            address: siteAddress.trim() || null,
            createdAt: serverTimestamp(),
            createdBy: user.uid,
          }
        );
      } catch (err) {
        console.error(err);
        setError('Failed to save — please try again');
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    if (step === 3) {
      setSaving(true);
      try {
        await updateDoc(
          doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
          { onboardingComplete: true, updatedAt: serverTimestamp() }
        );
        await refreshProfile();
        navigate('/dashboard');
      } catch (err) {
        console.error(err);
        setError('Failed to complete onboarding');
      }
      setSaving(false);
      return;
    }

    setStep(prev => prev + 1);
  };

  const initials = userProfile?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const INDUSTRY_TYPES = [
    { value: 'farm',        label: 'Farm'               },
    { value: 'estate',      label: 'Estate'             },
    { value: 'contractor',  label: 'Agricultural contractor' },
    { value: 'dealership',  label: 'Dealership'         },
    { value: 'groundcare',  label: 'Groundcare'         },
    { value: 'other',       label: 'Other'              },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{ width: '100%', maxWidth: 520 }}>

        {/* Header */}
        <div style={{
          background: 'var(--color-brand)',
          borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0',
          padding: '24px 28px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 36,
              height: 36,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <IconShieldCheck size={20} color="#fff" stroke={1.5} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 400, color: '#fff' }}>Agriware Ops</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>Account setup</div>
            </div>
          </div>

          {/* Step indicators */}
          <div style={{ display: 'flex', gap: 6 }}>
            {STEPS.map((s, i) => (
              <div key={i} style={{ flex: 1 }}>
                <div style={{
                  height: 2,
                  borderRadius: 1,
                  background: i <= step ? '#fff' : 'rgba(255,255,255,0.3)',
                  marginBottom: 4,
                }} />
                <div style={{
                  fontSize: 9,
                  color: i === step ? '#fff' : 'rgba(255,255,255,0.5)',
                  letterSpacing: '0.04em',
                }}>
                  {s.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{
          background: 'var(--color-bg-primary)',
          borderRadius: '0 0 var(--radius-xl) var(--radius-xl)',
          border: '0.5px solid var(--color-border-light)',
          borderTop: 'none',
          padding: '28px',
        }}>

          {error && (
            <div style={{
              background: 'var(--color-danger-light)',
              border: '0.5px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 14px',
              fontSize: 13,
              color: 'var(--color-danger)',
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          {/* Step 0 — Photo */}
          {step === 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 6 }}>
                Add your profile photo
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
                This helps your team recognise you across the platform
              </div>
              <div
                onClick={() => setShowCropModal(true)}
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: '50%',
                  background: photoURL ? 'transparent' : '#FDF0E8',
                  backgroundImage: photoURL ? `url(${photoURL})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  cursor: 'pointer',
                  fontSize: 28,
                  color: '#993C1D',
                  border: '2px solid transparent',
                  transition: 'border-color 0.15s',
                  position: 'relative',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
              >
                {!photoURL && initials}
                {photoURL && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: 'var(--color-brand)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <IconCamera size={13} color="#fff" stroke={1.5} />
                  </div>
                )}
              </div>
              <div
                onClick={() => setShowCropModal(true)}
                style={{ fontSize: 13, color: 'var(--color-brand)', cursor: 'pointer', marginBottom: 8 }}
              >
                {photoURL ? 'Change photo' : 'Upload photo'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                You can skip this and add it later
              </div>
            </div>
          )}

          {/* Step 1 — Org details */}
          {step === 1 && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 6 }}>
                Organisation details
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 20 }}>
                Tell us about your organisation
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                    Organisation name <span style={{ color: 'var(--color-danger)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Greenacre Estate"
                    value={orgName}
                    onChange={e => setOrgName(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                    Trading name <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span>
                  </label>
                  <input
                    type="text"
                    placeholder="If different"
                    value={tradingName}
                    onChange={e => setTradingName(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                  Industry type
                </label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {INDUSTRY_TYPES.map(t => (
                    <div
                      key={t.value}
                      onClick={() => setIndustryType(t.value)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: 'var(--radius-md)',
                        border: '0.5px solid',
                        borderColor: industryType === t.value ? 'var(--color-brand)' : 'var(--color-border-medium)',
                        background: industryType === t.value ? 'var(--color-brand-light)' : 'transparent',
                        color: industryType === t.value ? 'var(--color-brand)' : 'var(--color-text-secondary)',
                        fontSize: 12,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {t.label}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                    Company number <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span>
                  </label>
                  <input
                    type="text"
                    placeholder="12345678"
                    value={companyNumber}
                    onChange={e => setCompanyNumber(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                    VAT number <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span>
                  </label>
                  <input
                    type="text"
                    placeholder="GB123456789"
                    value={vatNumber}
                    onChange={e => setVatNumber(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                    Phone number
                  </label>
                  <input
                    type="tel"
                    placeholder="+44 1234 567890"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                    Website
                  </label>
                  <input
                    type="text"
                    placeholder="greenacre.co.uk"
                    value={website}
                    onChange={e => setWebsite(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                  Address line 1
                </label>
                <input
                  type="text"
                  placeholder="Farm House, Farm Lane"
                  value={address1}
                  onChange={e => setAddress1(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                    Town / City
                  </label>
                  <input
                    type="text"
                    placeholder="Aylesbury"
                    value={town}
                    onChange={e => setTown(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                    Postcode
                  </label>
                  <input
                    type="text"
                    placeholder="HP20 1AB"
                    value={postcode}
                    onChange={e => setPostcode(e.target.value.toUpperCase())}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — First site */}
          {step === 2 && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 6 }}>
                Your first site
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 20 }}>
                Add your main farm or site — you can add more later in settings
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                  Site name <span style={{ color: 'var(--color-danger)' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Home Farm"
                  value={siteName}
                  onChange={e => setSiteName(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 5 }}>
                  Site address <span style={{ color: 'var(--color-text-tertiary)' }}>— optional</span>
                </label>
                <input
                  type="text"
                  placeholder="Home Farm, Farm Lane, Aylesbury HP20 1AB"
                  value={siteAddress}
                  onChange={e => setSiteAddress(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          )}

          {/* Step 3 — Ready */}
          {step === 3 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'var(--color-success-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <IconCheck size={28} color="var(--color-success)" stroke={1.5} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 400, marginBottom: 8 }}>
                You're all set
              </div>
              <div style={{
                fontSize: 13,
                color: 'var(--color-text-tertiary)',
                lineHeight: 1.6,
                marginBottom: 8,
              }}>
                Welcome to Agriware Ops, {userProfile?.name?.split(' ')[0]}. Your organisation is ready. Start by onboarding your first machine.
              </div>
            </div>
          )}

          {/* Navigation */}
          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            {step > 0 && step < 3 && (
              <button
                onClick={() => setStep(prev => prev - 1)}
                style={{
                  flex: 1,
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  border: '0.5px solid var(--color-border-medium)',
                  borderRadius: 'var(--radius-md)',
                  padding: '10px',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={saving}
              style={{
                flex: 2,
                background: step === 3 ? 'var(--color-success)' : 'var(--color-brand)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                padding: '10px',
                fontSize: 13,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving...' : step === 3 ? 'Go to dashboard' : step === 0 ? 'Next' : 'Continue'}
            </button>
          </div>
        </div>
      </div>

      {showCropModal && (
        <PhotoCropModal
          onSave={handlePhotoSave}
          onClose={() => setShowCropModal(false)}
        />
      )}
    </div>
  );
}